/**
 * 이미지 최적화 유틸리티
 *
 * 이미지 로딩 성능을 개선하기 위한 유틸리티 모음:
 * - Intersection Observer를 활용한 지연 로딩 (Lazy Loading)
 * - WebP / AVIF 포맷 지원 및 폴백 처리
 * - srcset을 활용한 반응형 이미지
 * - Blur-up 플레이스홀더 기법
 * - 로딩 상태 및 에러 처리
 * - CDN 연동을 통한 동적 이미지 리사이징
 */

import { sanitizeURL } from './xssProtection';

// ─────────────────────────────────────────────────────────────
// 상수 정의
// ─────────────────────────────────────────────────────────────

/** 반응형 이미지에 사용하는 표준 크기 (px) */
export const IMAGE_SIZES = {
  thumbnail: 150,
  small: 300,
  medium: 600,
  large: 1200,
  xlarge: 2400,
} as const;

/** 이미지 품질 설정 (0–100) */
export const IMAGE_QUALITY = {
  low: 50,
  medium: 75,
  high: 85,     // 압축률 향상을 위해 90 → 85로 조정
  veryHigh: 90, // 중요 이미지에만 사용
} as const;

/** 반응형 이미지 생성에 사용하는 디바이스 픽셀 비율 목록 */
export const DEVICE_PIXEL_RATIOS = [1, 1.5, 2, 3] as const;

/** 화면 비율 프리셋 */
export const ASPECT_RATIOS = {
  square: '1 / 1',
  portrait: '3 / 4',
  landscape: '4 / 3',
  widescreen: '16 / 9',
  ultrawide: '21 / 9',
  albumCover: '1 / 1',
  card: '16 / 9',
} as const;

/** Lazy Load Intersection Observer 옵션 */
export const LAZY_LOAD_OPTIONS: IntersectionObserverInit = {
  root: null,
  rootMargin: '50px', // 뷰포트 진입 50px 전부터 로딩 시작
  threshold: 0.01,
};

// ─────────────────────────────────────────────────────────────
// 타입 정의
// ─────────────────────────────────────────────────────────────

/** 이미지 최적화 파라미터 */
export interface ImageOptimizationParams {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'avif' | 'webp' | 'jpeg' | 'png' | 'auto';
  fit?: 'cover' | 'contain' | 'fill' | 'inside' | 'outside';
  /** 플레이스홀더용 블러 강도 */
  blur?: number;
  /** 자동 최적화 방식 */
  auto?: 'format' | 'compress';
}

/** 이미지 로딩 우선순위 */
export type ImagePriority = 'high' | 'low' | 'auto';

/** 이미지 로딩 상태 */
export type ImageLoadingState = 'idle' | 'loading' | 'loaded' | 'error';

// ─────────────────────────────────────────────────────────────
// 브라우저 포맷 지원 감지
// ─────────────────────────────────────────────────────────────

/**
 * 브라우저가 특정 이미지 포맷을 지원하는지 canvas로 확인
 *
 * @param mimeType - 확인할 MIME 타입 (예: 'image/webp', 'image/avif')
 */
function supportsImageFormat(mimeType: string): boolean {
  const canvas = document.createElement('canvas');
  if (!canvas.getContext?.('2d')) return false;
  return canvas.toDataURL(mimeType).startsWith(`data:${mimeType}`);
}

/** 브라우저의 WebP 지원 여부 */
export const supportsWebP = (): boolean => supportsImageFormat('image/webp');

/** 브라우저의 AVIF 지원 여부 */
export const supportsAVIF = (): boolean => supportsImageFormat('image/avif');

// ─────────────────────────────────────────────────────────────
// URL 생성 유틸리티
// ─────────────────────────────────────────────────────────────

/**
 * 이미지 URL에 최적화 파라미터를 추가하여 반환
 *
 * data: / blob: URL은 그대로 반환하며, 파라미터가 없으면 원본 URL을 반환한다.
 */
export const optimizeImageUrl = (
  url: string,
  params: ImageOptimizationParams = {},
): string => {
  if (!url) return '';

  const sanitized = sanitizeURL(url);
  if (!sanitized) return '';

  // data URL / blob URL은 최적화 불필요
  if (sanitized.startsWith('data:') || sanitized.startsWith('blob:')) {
    return sanitized;
  }

  const queryParams = new URLSearchParams();

  if (params.width)  queryParams.set('w', params.width.toString());
  if (params.height) queryParams.set('h', params.height.toString());

  // 품질: 명시된 값 또는 크기에 따른 기본값 적용
  if (params.quality) {
    queryParams.set('q', params.quality.toString());
  } else if (params.width || params.height) {
    const defaultQuality = (params.width ?? params.height ?? 0) > 1200 ? 75 : 85;
    queryParams.set('q', defaultQuality.toString());
  }

  // 포맷 최적화
  if (params.format === 'auto') {
    queryParams.set('auto', 'format'); // 백엔드가 Accept 헤더 기준으로 결정
  } else if (params.format) {
    queryParams.set('format', params.format);
  }

  if (params.fit)  queryParams.set('fit', params.fit);
  if (params.blur) queryParams.set('blur', params.blur.toString());
  if (params.auto) queryParams.set('auto', params.auto);

  const queryString = queryParams.toString();
  if (!queryString) return sanitized;

  return sanitized.includes('?')
    ? `${sanitized}&${queryString}`
    : `${sanitized}?${queryString}`;
};

/** WebP 포맷 URL 반환 */
export const getWebPUrl = (imageUrl: string): string =>
  optimizeImageUrl(imageUrl, { format: 'webp' });

/** Blur-up 플레이스홀더 URL 반환 (저품질 축소 이미지) */
export const getBlurPlaceholder = (imageUrl: string): string =>
  optimizeImageUrl(imageUrl, { width: 20, quality: 10, blur: 10 });

/**
 * srcset 속성 문자열 생성
 *
 * baseWidth가 있으면 DEVICE_PIXEL_RATIOS 기준으로 크기를 계산하고,
 * 없으면 기본 크기 목록 [300, 600, 900, 1200]을 사용한다.
 *
 * @param baseUrl     - 원본 이미지 URL
 * @param baseWidth   - 기준 너비 (px), 생략 시 기본 목록 사용
 * @param customSizes - 직접 지정하는 크기 목록
 * @param format      - 이미지 포맷 (생략 시 원본 유지)
 */
export const generateSrcSet = (
  baseUrl: string,
  baseWidth?: number,
  customSizes?: number[],
  format?: 'avif' | 'webp' | 'jpeg' | 'png',
): string => {
  const sizes = customSizes ?? (
    baseWidth
      ? DEVICE_PIXEL_RATIOS.map(ratio => Math.floor(baseWidth * ratio))
      : [300, 600, 900, 1200]
  );

  return sizes
    .map(size => `${optimizeImageUrl(baseUrl, { width: size, ...(format && { format }) })} ${size}w`)
    .join(', ');
};

/**
 * sizes 속성 문자열 생성
 *
 * breakpoints를 지정하지 않으면 기본 반응형 크기를 반환한다.
 *
 * @param maxWidth    - 최대 너비 (px)
 * @param breakpoints - 미디어쿼리 → 크기 매핑 (예: { '640px': '100vw' })
 */
export const generateSizesAttribute = (
  maxWidth: number,
  breakpoints?: Record<string, string>,
): string => {
  if (breakpoints) {
    return Object.entries(breakpoints)
      .map(([bp, size]) => `(max-width: ${bp}) ${size}`)
      .join(', ');
  }

  // 기본 반응형 크기
  return `(max-width: 640px) 100vw, (max-width: 1024px) 50vw, ${maxWidth}px`;
};

// ─────────────────────────────────────────────────────────────
// 이미지 프리로드
// ─────────────────────────────────────────────────────────────

/** 단일 이미지 프리로드 (Promise 기반) */
export const preloadImage = (url: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = sanitizeURL(url) || '';
  });

/** 여러 이미지 동시 프리로드 */
export const preloadImages = (urls: string[]): Promise<void[]> =>
  Promise.all(urls.map(preloadImage));

// ─────────────────────────────────────────────────────────────
// 지연 로딩 (Lazy Loading)
// ─────────────────────────────────────────────────────────────

/**
 * Intersection Observer 기반 지연 로딩 옵저버 생성
 *
 * 뷰포트에 진입한 요소만 콜백을 호출한다.
 */
export const createLazyLoadObserver = (
  callback: (entry: IntersectionObserverEntry) => void,
): IntersectionObserver =>
  new IntersectionObserver(
    (entries) => entries.forEach((entry) => {
      if (entry.isIntersecting) callback(entry);
    }),
    LAZY_LOAD_OPTIONS,
  );

/**
 * 요소가 첫 뷰포트 아래에 있으면 true (지연 로딩 필요)
 *
 * 뷰포트 안에 이미 보이는 이미지는 지연 로딩 대상에서 제외한다.
 */
export const shouldLazyLoad = (element: HTMLElement | null): boolean => {
  if (!element) return true;

  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  // 뷰포트 안에 있으면 지연 로딩 불필요
  return rect.top > viewportHeight;
};

// ─────────────────────────────────────────────────────────────
// 기타 유틸리티
// ─────────────────────────────────────────────────────────────

/** 너비·높이로 CSS aspect-ratio 문자열 계산 */
export const calculateAspectRatio = (width: number, height: number): string =>
  `${width} / ${height}`;

/**
 * URL 확장자로 이미지 포맷 감지
 *
 * 유효하지 않은 포맷이거나 URL이 없으면 null 반환
 */
export const detectImageFormat = (url: string): string | null => {
  if (!url) return null;

  const extension = url.split('.').pop()?.toLowerCase();
  const validFormats = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg', 'avif'];

  return extension && validFormats.includes(extension) ? extension : null;
};

/**
 * 우선순위에 따른 loading 속성 값 반환
 *
 * - 'high' → 'eager' (즉시 로드)
 * - 그 외  → 'lazy'  (지연 로드)
 */
export const getLoadingAttribute = (priority: ImagePriority = 'auto'): 'eager' | 'lazy' =>
  priority === 'high' ? 'eager' : 'lazy';
